// src/modules/competitions/pages/CompetitionsList.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import CompetitionCard from '../components/CompetitionCard';
import { Competition, CompetitionDetails, CompetitionFilters, EventStatus } from '../types';
import { competitionService } from '../services/competitionService';
import { useAppContext } from '../../../shared/context/AppContext';
import Button from '../../../shared/components/ui/Button';
import '../styles/competitions.css';

const CompetitionsList: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { auth } = state;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [competitions, setCompetitions] = useState<CompetitionDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCompetitions, setTotalCompetitions] = useState(0);
  const [filters, setFilters] = useState<CompetitionFilters>({
    status: undefined,
    category_id: undefined,
    limit: 12,
    offset: 0
  });

  // Optimización con useCallback
  const loadCompetitions = useCallback(async (reset: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const currentFilters = reset ? { ...filters, offset: 0 } : filters;
      const response = await competitionService.getCompetitions(currentFilters);

      if (response.success && response.data) {
        const newCompetitions = response.data.competitions;

        setCompetitions(prev =>
          reset ? newCompetitions : [...prev, ...newCompetitions]
        );

        setHasMore(response.data.pagination.has_more);
        setTotalCompetitions(response.data.pagination.total);

        if (!reset) {
          setFilters(prev => ({
            ...prev,
            offset: (prev.offset || 0) + (prev.limit || 12)
          }));
        }
      } else {
        setError(response.message || 'Error al cargar competencias');
      }
    } catch (err) {
      setError('Error al cargar competencias');
      console.error('Error loading competitions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadCompetitions(true);
    setIsRefreshing(false);
  };

  const handleGoBack = () => navigate(-1);

  useEffect(() => {
    loadCompetitions(true);
  }, [filters.status, filters.category_id]);

  const handleStatusFilter = (status: EventStatus | undefined) => {
    setFilters(prev => ({ ...prev, status, offset: 0 }));
  };

  const handleViewDetails = (competitionId: number) => {
  const competitionDetails = competitions.find(c => c.competition.id === competitionId);
  navigate(`/competitions/${competitionId}`, {
    state: { competitionDetails }  // ✅ Pasa los datos
  });
};

  const handleRegister = (competitionId: number) => {
    navigate(`/competitions/${competitionId}/register`);
  };

  const handleLoadMore = () => {
    loadCompetitions(false);
  };

  // Estadísticas calculadas
  const stats = {
    total: totalCompetitions,
    active: competitions.filter(c => c.competition.status === 'active').length,
    upcoming: competitions.filter(c => c.competition.status === 'upcoming').length
  };

  const filterButtons = [
    { label: 'Todas', status: undefined },
    { label: 'Próximas', status: 'upcoming' as EventStatus },
    { label: 'En curso', status: 'active' as EventStatus },
    { label: 'Finalizadas', status: 'completed' as EventStatus }
  ];

  return (
    <div className="competitions-list-page">
      {/* Header con acciones */}
      <div className="page-actions">
        <Button className="back-btn" onClick={() => navigate('/competitions')}>
          ← Volver
        </Button>
      </div>

      {/* Header principal */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="header-title">Competencias iBloom</h1>
          <p className="header-subtitle">
            Descubre y participa en nuestras competencias de belleza y creatividad
          </p>
        </div>

        {/* Estadísticas */}
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Disponibles</span>
          </div>
          <div className="stat-item stat-active">
            <span className="stat-number">{stats.active}</span>
            <span className="stat-label">En curso</span>
          </div>
          <div className="stat-item stat-upcoming">
            <span className="stat-number">{stats.upcoming}</span>
            <span className="stat-label">Próximas</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card className="filters-card" variant="flat">
        <div className="filters-section">
          <h3 className="filters-title">Filtrar competencias</h3>
          <div className="filter-group">
            <label className="filter-label">Estado:</label>
            <div className="filter-buttons">
              {filterButtons.map(({ label, status }) => (
                <Button
                  key={label}
                  className={`filter-btn ${filters.status === status ? 'active' : ''}`}
                  onClick={() => handleStatusFilter(status)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Mensajes de error */}
      {error && (
        <ErrorMessage
          message={error}
          type="error"
          onClose={() => setError(null)}
        />
      )}

      {/* Contenido principal */}
      {isLoading && competitions.length === 0 ? (
        <Loading message="Cargando competencias..." />
      ) : competitions.length === 0 ? (
        <EmptyState
          filters={filters}
          onClearFilter={() => handleStatusFilter(undefined)}
        />
      ) : (
        <>
          <div className="competitions-grid">
            {competitions.map((competitionDetails) => (
              <CompetitionCard
                key={competitionDetails.competition.id}
                competition={competitionDetails.competition}
                onViewDetails={handleViewDetails}
                onRegister={auth.isAuthenticated ? handleRegister : undefined}
                showActions={true}
              />
            ))}
          </div>

          {/* Cargar más */}
          {hasMore && (
            <div className="load-more-section">
              <Button
                className="btn btn-outline btn-lg"
                onClick={handleLoadMore}
                disabled={isLoading}
              >
                {isLoading ? 'Cargando...' : 'Cargar más competencias'}
              </Button>
            </div>
          )}

          {/* Info de resultados */}
          <div className="results-info">
            <p>
              Mostrando <strong>{competitions.length}</strong> de{' '}
              <strong>{totalCompetitions}</strong> competencias
            </p>
          </div>
        </>
      )}

      {/* CTA para usuarios no autenticados */}
      {!auth.isAuthenticated && (
        <Card className="auth-cta" variant="glass">
          <div className="cta-content">
            <div className="cta-icon">🌟</div>
            <h3>¿Quieres participar?</h3>
            <p>
              Inicia sesión para registrarte en nuestras competencias y mostrar tu talento.
            </p>
            <Button
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/login')}
            >
              Iniciar sesión
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

// Componente EmptyState separado
const EmptyState: React.FC<{
  filters: CompetitionFilters;
  onClearFilter: () => void;
}> = ({ filters, onClearFilter }) => (
  <Card className="empty-state" variant="flat">
    <div className="empty-content">
      <div className="empty-icon">🏆</div>
      <h3>No hay competencias disponibles</h3>
      <p>
        {filters.status
          ? `No se encontraron competencias con el estado "${filters.status}"`
          : 'Actualmente no hay competencias disponibles.'}
      </p>
      {filters.status && (
        <Button className="btn btn-primary" onClick={onClearFilter}>
          Ver todas las competencias
        </Button>
      )}
    </div>
  </Card>
);

export default CompetitionsList;